// =============================================================================
// Hoop Flo — Size Quiz (embed + results)
// File: assets/sizeguide-quiz.js
// Table is loaded dynamically from Vercel /api/table (fetched from Google Sheet)
// =============================================================================

(function () {

  const PROXY_BASE = 'https://sizeguide-proxy.vercel.app';

  // TABLE is loaded async on init — null until ready
  let TABLE = null;

  // ── NORMALISATION MAPS ───────────────────────────────────────────────────────

  const SKILL_MAP = {
    'totally – i had no idea there were so many sizes!': 'newbie',
    'totally': 'newbie',
    'newbie': 'newbie',
    "beginner – i've dabbled, but still learning the basics.": 'beginner',
    'beginner': 'beginner',
    "intermediate & beyond – nope, i've been doing this for a while.": 'intermediate',
    'intermediate & beyond': 'intermediate',
    'intermediate': 'intermediate',
  };

  const STYLE_MAP = {
    'hooping on different body parts like waist, shoulders, neck, and knees.': 'on-body',
    'on-body': 'on-body',
    'learning tricks like rolls, folds, weaves, escalators, or tosses.': 'off-body',
    'off-body': 'off-body',
    'hoop dancing and flow': 'dance',
    'hoop dancing & flow': 'dance',
    'dance': 'dance',
    'using multiple hoops, like twins/doubles or juggling': 'multihooping',
    'multihooping': 'multihooping',
    'hoop balancing': 'balance',
    'balance': 'balance',
    "i'm not sure yet / all of the above!": 'unsure',
    'unsure / all of the above!': 'unsure',
    'unsure': 'unsure',
  };

  const WAIST_MAP = {
    // Typeform labels (after asterisk stripping)
    '(xxs) less than 24 inches': 'XXS',
    '(xxs) less than 24"': 'XXS',
    'less than 24 inches': 'XXS',
    'less than 24"': 'XXS',
    'xxs': 'XXS',
    '(xs – s) 24" – 30"': 'XS-S',
    '24" – 30"': 'XS-S',
    'xs – s': 'XS-S',
    '(m – l) 30" – 36"': 'M-L',
    '30" – 36"': 'M-L',
    'm – l': 'M-L',
    '(xl+) more than 36 inches': 'XL+',
    '(xl+) more than 36"': 'XL+',
    'more than 36 inches': 'XL+',
    'more than 36"': 'XL+',
    'xl+': 'XL+',
  };

  const HEIGHT_MAP = {
    'less than 4 ft 8 in': 'P',
    'less than 4ft 8in': 'P',
    'less than 4’8”': 'P',
    '4’8” – 5’3”': 'S',
    '5’3” – 5’9”': 'M',
    '5’3” – 5’9’”': 'M',
    'more than 5 ft 9 in': 'T',
    'more than 5ft 9in': 'T',
    'more than 5’9”': 'T',
  };

  const MULTI_MAP = {
    'tech & tricks with 2 hoops': '2-hoops',
    'tech & tricks with 3+ hoops': '3plus-hoops',
    'juggling': 'juggling',
    'on-body splits': 'splits',
  };

  // ── HELPERS ──────────────────────────────────────────────────────────────────

  function renderSize(val) {
    if (val === null || val === undefined) return null;
    const arr = Array.isArray(val) ? val : [val];
    return arr.map(n => n + '"').join(' or ');
  }

  function setText(id, text) {
    const el = document.getElementById(id);
    if (el && text !== null && text !== undefined) el.textContent = text;
  }

  function show(id) {
    const el = document.getElementById(id);
    if (el) el.removeAttribute('data-hoop-hidden');
  }

  function hide(id) {
    const el = document.getElementById(id);
    if (el) el.setAttribute('data-hoop-hidden', '');
  }

  function getAnswer(answers, ref) {
    const a = answers.find(a => a.field && a.field.ref === ref);
    if (!a) return null;
    const clean = str => str ? str.replace(/\*/g, '').replace(/\s+/g, ' ').trim() : null;
    if (a.choice)  return clean(a.choice.label) ?? null;
    if (a.choices) return a.choices.labels?.map(clean) ?? null;
    if (a.text)    return a.text ?? null;
    if (a.boolean !== undefined) return a.boolean ? 'Yes' : 'No';
    return null;
  }

  // ── RENDER RESULTS ────────────────────────────────────────────────────────

  function renderResults(data, context) {
    hide('hoop-loading');

    if (!data) {
      show('hoop-error');
      setText('hoop-error-text',
        "We couldn't find a match for your answers — sorry! Please try again or contact us. (ref: " + context.key + ")"
      );
      return;
    }

    setText('hoop-skill', context.rawLevel);
    setText('hoop-style', context.rawStyle);

    if (data.type === 'starter') {
      show('hoop-starter-primary');
      setText('hoop-starter-primary-little', renderSize(data.starterLittle));
      setText('hoop-starter-primary-big',    renderSize(data.starterBig));
      if (data.rec2) {
        show('hoop-single-fallback');
        setText('hoop-single-fallback-size', renderSize(data.rec2));
      }
    } else {
      show('hoop-rec-primary');
      setText('hoop-rec1', renderSize(data.rec1));
      if (data.rec2) {
        show('hoop-rec2-row');
        setText('hoop-rec2', renderSize(data.rec2));
      }
      if (data.starterLittle && data.starterBig) {
        const skillKey = SKILL_MAP[context.rawLevel.toLowerCase().trim()];
        if (skillKey === 'newbie' || skillKey === 'beginner') {
          show('hoop-starter-secondary');
          setText('hoop-starter-secondary-little', renderSize(data.starterLittle));
          setText('hoop-starter-secondary-big',    renderSize(data.starterBig));
        }
      }
    }

    if (context.hoopFeel) {
      const feel = context.hoopFeel.toLowerCase().trim();
      let feelText = null;
      if (feel === 'too big') {
        feelText = 'Since your current hoop feels too big, you might find the smaller end of this range suits you better.';
      } else if (feel === 'too small') {
        feelText = 'Since your current hoop feels too small, you might find the larger end of this range suits you better.';
      }
      if (feelText) {
        show('hoop-contextual-feel');
        setText('hoop-contextual-feel-text', feelText);
      }
    }

    if (context.challenge) {
      show('hoop-contextual-challenge');
      setText('hoop-contextual-challenge-text', context.challenge);
    }

    show('hoop-result');

    const resultsEl = document.getElementById('hoop-result');
    if (resultsEl) {
      setTimeout(() => {
        resultsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }

  // ── PROCESS ANSWERS ───────────────────────────────────────────────────────

  function processAnswers(answers) {
    const rawLevel  = getAnswer(answers, 'level')             ?? '';
    const rawHeight = getAnswer(answers, 'height')            ?? '';
    const rawWaist  = getAnswer(answers, 'waist')             ?? '';
    const rawStyle  = getAnswer(answers, 'style')             ?? '';
    const rawMulti  = getAnswer(answers, 'multistyle')        ?? '';
    const hoopFeel  = getAnswer(answers, 'currenthoop-feel')  ?? '';
    const hoopSize  = getAnswer(answers, 'currenthoop-size')  ?? '';
    const challenge = getAnswer(answers, 'biggest-challenge') ?? '';
    const currentHoop = getAnswer(answers, 'currenthoop')     ?? '';

    const skillKey  = SKILL_MAP[rawLevel.toLowerCase().trim()]   ?? rawLevel.toLowerCase().trim();
    const styleKey  = STYLE_MAP[rawStyle.toLowerCase().trim()]   ?? rawStyle.toLowerCase().trim();
    const waistKey  = WAIST_MAP[rawWaist.toLowerCase().trim()]   ?? rawWaist.trim();
    const heightKey = HEIGHT_MAP[rawHeight.toLowerCase().trim()]  ?? rawHeight.trim();
    const multiKey  = rawMulti ? (MULTI_MAP[rawMulti.toLowerCase().trim()] ?? null) : null;

    let key = [skillKey, styleKey, waistKey, heightKey].join('|');
    if (multiKey) key += '|' + multiKey;

    const data = TABLE ? (TABLE[key] ?? null) : null;
    if (!TABLE) {
      show('hoop-error');
      setText('hoop-error-text', 'Results table is still loading — please wait a moment and try again.');
      hide('hoop-loading');
      return;
    }

    renderResults(data, {
      key, rawLevel, rawStyle, rawHeight, rawWaist,
      hoopFeel, hoopSize, challenge, currentHoop
    });
  }

  // ── TYPEFORM CALLBACK ─────────────────────────────────────────────────────
  // Called by Typeform embed on submission.
  // Fetches full response from Responses API using the responseId.

  window.__hoopFloOnSubmit = async function({ formId, responseId }) {
    show('hoop-loading');
    hide('hoop-result');
    hide('hoop-error');

    // Small delay to allow Typeform API to process the response
    await new Promise(resolve => setTimeout(resolve, 2000));

    try {
      const res = await fetch(
        `${PROXY_BASE}/api/proxy?formId=${formId}&responseId=${responseId}`
      );

      if (!res.ok) {
        throw new Error('API request failed: ' + res.status);
      }

      const json = await res.json();
      const answers = json.items?.[0]?.answers ?? [];

      if (answers.length === 0) {
        throw new Error('No answers found in response');
      }

      processAnswers(answers);

    } catch (err) {
      hide('hoop-loading');
      show('hoop-error');
      setText('hoop-error-text',
        'Something went wrong loading your result. Please try again or contact us. (' + err.message + ')'
      );
      console.error('Hoop Flo quiz error:', err);
    }
  };

  // ── CONSOLE TEST HELPER ───────────────────────────────────────────────────
  // Call this from the browser console to test rendering without submitting:
  //
  // window.__hoopFloTest({
  //   level: "Beginner – I've dabbled, but still learning the basics.",
  //   height: "4'8\" – 5'3\"",
  //   waist: '(XS – S) 24" – 30"',
  //   style: 'learning tricks like rolls, folds, weaves, escalators, or tosses.',
  //   hoopFeel: 'too big',
  //   challenge: "My current hoop(s) feels too big/slow"
  // });

  window.__hoopFloTest = function(opts) {
    const answers = Object.entries({
      level:              opts.level     || '',
      height:             opts.height    || '',
      waist:              opts.waist     || '',
      style:              opts.style     || '',
      multistyle:         opts.multi     || '',
      'currenthoop':      opts.currentHoop || 'No',
      'currenthoop-feel': opts.hoopFeel  || '',
      'currenthoop-size': opts.hoopSize  || '',
      'biggest-challenge':opts.challenge || '',
    }).map(([ref, label]) => ({
      field: { ref },
      choice: { label }
    }));
    hide('hoop-result');
    hide('hoop-error');
    processAnswers(answers);
  };

  // ── RETAKE BUTTON ─────────────────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', function() {
    const btn = document.getElementById('hoop-retake-btn');
    if (btn) btn.addEventListener('click', () => window.location.reload());
  });

})();